export const VOC_WORD_CLOZE_STORAGE_KEY = 'vocWordClozePayload'
export const VOC_WORD_CLOZE_PENDING_TAGS_KEY = 'vocWordClozePendingTags'
export const DEFAULT_CLOZE_TIME_LIMIT_SECONDS = 45
export const DIGEST_EXAMPLE_SENTENCE_TAG_ID = 9
export const CLOZE_TYPE = '选词填空'
export const COMPLETION_TYPE = '完形填空'

export function isCompletionType (item) {
  return String(item?.type || '') === COMPLETION_TYPE
}

export function isClozeType (item) {
  return !isCompletionType(item)
}

export function readPendingClozeTags () {
  try {
    const raw = sessionStorage.getItem(VOC_WORD_CLOZE_PENDING_TAGS_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

export function appendPendingClozeTag (entry) {
  const list = readPendingClozeTags()
  const exists = list.some(
    (t) =>
      Number(t.vocabularyId) === Number(entry.vocabularyId) &&
      Number(t.vocTagId) === Number(entry.vocTagId) &&
      Number(t.userId) === Number(entry.userId)
  )
  if (!exists) list.push(entry)
  sessionStorage.setItem(VOC_WORD_CLOZE_PENDING_TAGS_KEY, JSON.stringify(list))
}

export function removePendingClozeTags (entries) {
  if (!entries.length) return
  const removeKeys = new Set(
    entries.map((e) => `${e.vocabularyId}-${e.vocTagId}-${e.userId}`)
  )
  const remaining = readPendingClozeTags().filter(
    (t) => !removeKeys.has(`${t.vocabularyId}-${t.vocTagId}-${t.userId}`)
  )
  if (remaining.length) {
    sessionStorage.setItem(VOC_WORD_CLOZE_PENDING_TAGS_KEY, JSON.stringify(remaining))
  } else {
    sessionStorage.removeItem(VOC_WORD_CLOZE_PENDING_TAGS_KEY)
  }
}

/** Common irregular / extended forms keyed by normalized base lemma */
const IRREGULAR_FORMS = {
  be: ['am', 'is', 'are', 'was', 'were', 'been', 'being'],
  go: ['goes', 'going', 'went', 'gone'],
  do: ['does', 'doing', 'did', 'done'],
  have: ['has', 'having', 'had'],
  say: ['says', 'saying', 'said'],
  get: ['gets', 'getting', 'got', 'gotten'],
  make: ['makes', 'making', 'made'],
  know: ['knows', 'knowing', 'knew', 'known'],
  think: ['thinks', 'thinking', 'thought'],
  take: ['takes', 'taking', 'took', 'taken'],
  see: ['sees', 'seeing', 'saw', 'seen'],
  come: ['comes', 'coming', 'came'],
  give: ['gives', 'giving', 'gave', 'given'],
  find: ['finds', 'finding', 'found'],
  tell: ['tells', 'telling', 'told'],
  become: ['becomes', 'becoming', 'became'],
  leave: ['leaves', 'leaving', 'left'],
  feel: ['feels', 'feeling', 'felt'],
  bring: ['brings', 'bringing', 'brought'],
  begin: ['begins', 'beginning', 'began', 'begun'],
  keep: ['keeps', 'keeping', 'kept'],
  hold: ['holds', 'holding', 'held'],
  write: ['writes', 'writing', 'wrote', 'written'],
  read: ['reads', 'reading'],
  run: ['runs', 'running', 'ran'],
  speak: ['speaks', 'speaking', 'spoke', 'spoken'],
  buy: ['buys', 'buying', 'bought'],
  send: ['sends', 'sending', 'sent'],
  build: ['builds', 'building', 'built'],
  sit: ['sits', 'sitting', 'sat'],
  stand: ['stands', 'standing', 'stood'],
  lose: ['loses', 'losing', 'lost'],
  pay: ['pays', 'paying', 'paid'],
  meet: ['meets', 'meeting', 'met'],
  hear: ['hears', 'hearing', 'heard'],
  grow: ['grows', 'growing', 'grew', 'grown'],
  fall: ['falls', 'falling', 'fell', 'fallen'],
  cut: ['cuts', 'cutting'],
  put: ['puts', 'putting'],
  hit: ['hits', 'hitting'],
  set: ['sets', 'setting'],
  hurt: ['hurts', 'hurting'],
  let: ['lets', 'letting'],
  shut: ['shuts', 'shutting'],
  spread: ['spreads', 'spreading'],
  cost: ['costs', 'costing'],
  win: ['wins', 'winning', 'won'],
  hang: ['hangs', 'hanging', 'hung'],
  light: ['lights', 'lighting', 'lit'],
  mean: ['means', 'meaning', 'meant'],
  lead: ['leads', 'leading', 'led'],
  break: ['breaks', 'breaking', 'broke', 'broken'],
  choose: ['chooses', 'choosing', 'chose', 'chosen'],
  drive: ['drives', 'driving', 'drove', 'driven'],
  eat: ['eats', 'eating', 'ate', 'eaten'],
  fly: ['flies', 'flying', 'flew', 'flown'],
  forget: ['forgets', 'forgetting', 'forgot', 'forgotten'],
  hide: ['hides', 'hiding', 'hid', 'hidden'],
  lie: ['lies', 'lying', 'lay', 'lain'],
  ride: ['rides', 'riding', 'rode', 'ridden'],
  rise: ['rises', 'rising', 'rose', 'risen'],
  sell: ['sells', 'selling', 'sold'],
  sing: ['sings', 'singing', 'sang', 'sung'],
  sink: ['sinks', 'sinking', 'sank', 'sunk'],
  swim: ['swims', 'swimming', 'swam', 'swum'],
  teach: ['teaches', 'teaching', 'taught'],
  throw: ['throws', 'throwing', 'threw', 'thrown'],
  wake: ['wakes', 'waking', 'woke', 'woken'],
  wear: ['wears', 'wearing', 'wore', 'worn'],
  draw: ['draws', 'drawing', 'drew', 'drawn'],
  drink: ['drinks', 'drinking', 'drank', 'drunk'],
  blow: ['blows', 'blowing', 'blew', 'blown'],
  catch: ['catches', 'catching', 'caught'],
  fight: ['fights', 'fighting', 'fought'],
  forgive: ['forgives', 'forgiving', 'forgave', 'forgiven'],
  freeze: ['freezes', 'freezing', 'froze', 'frozen'],
  steal: ['steals', 'stealing', 'stole', 'stolen'],
  tear: ['tears', 'tearing', 'tore', 'torn'],
  child: ['children'],
  person: ['people', 'persons'],
  man: ['men'],
  woman: ['women'],
  foot: ['feet'],
  tooth: ['teeth'],
  mouse: ['mice'],
  goose: ['geese'],
  ox: ['oxen'],
  leaf: ['leaves'],
  life: ['lives'],
  wife: ['wives'],
  knife: ['knives'],
  wolf: ['wolves'],
  half: ['halves'],
  shelf: ['shelves'],
  thief: ['thieves'],
  potato: ['potatoes'],
  tomato: ['tomatoes'],
  hero: ['heroes'],
  echo: ['echoes'],
  volcano: ['volcanoes'],
  analysis: ['analyses'],
  basis: ['bases'],
  crisis: ['crises'],
  diagnosis: ['diagnoses'],
  hypothesis: ['hypotheses'],
  oasis: ['oases'],
  thesis: ['theses'],
  criterion: ['criteria'],
  phenomenon: ['phenomena'],
  bacterium: ['bacteria'],
  datum: ['data'],
  medium: ['media'],
  good: ['better', 'best'],
  bad: ['worse', 'worst'],
  many: ['more', 'most'],
  much: ['more', 'most'],
  little: ['less', 'least'],
  far: ['farther', 'further', 'furthest', 'farthest']
}

export function extractExampleSentences (chineseText) {
  if (!chineseText) return []
  let text = String(chineseText).replace(/<span[^>]*>.*?<\/span>/g, '')
  text = text.replace(/<span[^>]*>.*?<\/span>/g, '')
  const marker = '例句：'
  const indices = []
  for (const match of text.matchAll(new RegExp(marker, 'g'))) {
    indices.push(match.index + marker.length)
  }
  const result = []
  for (let i = 0; i < indices.length; i++) {
    const start = indices[i]
    const end = findExampleSentenceEnd(text, start)
    result.push(text.substring(start, end).trim())
  }
  return result.filter((s) => s.length > 0)
}

function findExampleSentenceEnd (text, start) {
  let end = text.length
  const boundaries = ['<br', '<BR', '<div', '<p', '\n例句：', '释义：', '备注：']
  for (const boundary of boundaries) {
    const idx = text.indexOf(boundary, start)
    if (idx !== -1 && idx < end) end = idx
  }
  const tagIdx = text.indexOf('<', start)
  if (tagIdx !== -1 && tagIdx < end) end = tagIdx
  return end
}

export function getBaseWordCandidates (englishText) {
  const raw = String(englishText || '').trim()
  if (!raw) return []
  const candidates = new Set()
  const withoutParen = raw.replace(/\([^)]*\)/g, ' ').replace(/\[[^\]]*\]/g, ' ')
  withoutParen.split(/[/|,;]+/).forEach((part) => {
    const cleaned = part.trim().replace(/^[^a-zA-Z]+|[^a-zA-Z'-]+$/g, '')
    if (cleaned) candidates.add(cleaned)
  })
  if (!candidates.size) candidates.add(raw)
  return [...candidates]
}

export function getPrimaryBaseWord (englishText) {
  const candidates = getBaseWordCandidates(englishText)
  return candidates[0] || String(englishText || '').trim()
}

function normalizeToken (token) {
  return String(token || '').toLowerCase().replace(/[^a-z]/g, '')
}

function getFormsForBase (baseWord) {
  const b = normalizeToken(baseWord)
  if (!b) return new Set()
  const forms = new Set([b])
  const irregular = IRREGULAR_FORMS[b] || []
  irregular.forEach((f) => forms.add(normalizeToken(f)))
  Object.entries(IRREGULAR_FORMS).forEach(([lemma, variants]) => {
    if (lemma === b || variants.some((v) => normalizeToken(v) === b)) {
      forms.add(normalizeToken(lemma))
      variants.forEach((v) => forms.add(normalizeToken(v)))
    }
  })
  return forms
}

export function wordMatchesBase (token, baseWord) {
  const t = normalizeToken(token)
  const candidates = getBaseWordCandidates(baseWord)
  if (!t || !candidates.length) return false

  return candidates.some((candidate) => {
    const b = normalizeToken(candidate)
    if (!b) return false
    if (t === b) return true
    if (t.startsWith(b) && t.length - b.length <= 4) return true

    const stem = b.endsWith('e') ? b.slice(0, -1) : b
    const variants = [
      b + 's',
      b + 'es',
      b + 'ed',
      b + 'ing',
      b + 'er',
      b + 'est',
      stem + 'ing',
      stem + 'ed',
      stem + 's',
      stem + 'es',
      b.endsWith('y') ? b.slice(0, -1) + 'ied' : null,
      b.endsWith('y') ? b.slice(0, -1) + 'ies' : null,
      b.endsWith('f') ? b.slice(0, -1) + 'ves' : null,
      b.endsWith('fe') ? b.slice(0, -2) + 'ves' : null
    ].filter(Boolean)
    if (variants.includes(t)) return true

    const forms = getFormsForBase(candidate)
    return forms.has(t)
  })
}

export function findLiteralWordInSentence (sentence, baseWord) {
  if (!sentence || !baseWord) return null
  const candidates = getBaseWordCandidates(baseWord)
  for (const candidate of candidates) {
    const escaped = candidate.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const re = new RegExp(`\\b${escaped}\\b`, 'i')
    const match = sentence.match(re)
    if (match) {
      return { form: match[0], start: match.index, end: match.index + match[0].length }
    }
  }
  return null
}

export function findWordFormInSentence (sentence, baseWord) {
  if (!sentence || !baseWord) return null

  const trimmedBase = String(baseWord).trim()
  const candidates = getBaseWordCandidates(trimmedBase)

  for (const candidate of candidates) {
    if (candidate.includes(' ')) {
      const escaped = candidate.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const re = new RegExp(escaped, 'i')
      const match = sentence.match(re)
      if (match) {
        return { form: match[0], start: match.index, end: match.index + match[0].length }
      }
    }
  }

  const tokenRe = /\b[\w'-]+\b/g
  let tokenMatch
  while ((tokenMatch = tokenRe.exec(sentence)) !== null) {
    if (wordMatchesBase(tokenMatch[0], trimmedBase)) {
      return {
        form: tokenMatch[0],
        start: tokenMatch.index,
        end: tokenMatch.index + tokenMatch[0].length
      }
    }
  }

  return findLiteralWordInSentence(sentence, trimmedBase)
}

export function resolveClozeSlot (sentence, englishText) {
  const baseWord = String(englishText || '').trim()
  const wordMatch = findWordFormInSentence(sentence, baseWord)
  if (wordMatch) {
    const parts = splitSentenceAroundWord(sentence, wordMatch)
    return {
      baseWord,
      sentenceBefore: parts.before,
      sentenceAfter: parts.after,
      correctForm: wordMatch.form,
      matchedInSentence: true
    }
  }

  const primary = getPrimaryBaseWord(baseWord)
  return {
    baseWord,
    sentenceBefore: `${sentence} `,
    sentenceAfter: '',
    correctForm: primary,
    matchedInSentence: false
  }
}

export function splitSentenceAroundWord (sentence, wordMatch) {
  return {
    before: sentence.slice(0, wordMatch.start),
    after: sentence.slice(wordMatch.end)
  }
}

export function chunkArray (arr, size) {
  const chunks = []
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size))
  }
  return chunks
}

/** If the last chunk has only one item, merge it into the previous group. */
export function chunkArrayForCloze (arr, size) {
  const chunks = chunkArray(arr, size)
  if (chunks.length >= 2 && chunks[chunks.length - 1].length === 1) {
    const lone = chunks.pop()
    chunks[chunks.length - 1] = chunks[chunks.length - 1].concat(lone)
  }
  return chunks
}

export function shuffleArray (arr) {
  const copy = [...arr]
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[copy[i], copy[j]] = [copy[j], copy[i]]
  }
  return copy
}

/** Split correct form into unchanged vs changed parts relative to base word. */
export function diffWordFormSegments (baseWord, correctForm) {
  const base = getPrimaryBaseWord(baseWord)
  const correct = String(correctForm || '')
  if (!correct) return []
  if (base.toLowerCase() === correct.toLowerCase()) {
    return [{ text: correct, changed: false }]
  }

  let prefixLen = 0
  const maxPrefix = Math.min(base.length, correct.length)
  while (
    prefixLen < maxPrefix &&
    base[prefixLen].toLowerCase() === correct[prefixLen].toLowerCase()
  ) {
    prefixLen++
  }

  let suffixLen = 0
  const maxSuffix = Math.min(base.length - prefixLen, correct.length - prefixLen)
  while (
    suffixLen < maxSuffix &&
    base[base.length - 1 - suffixLen].toLowerCase() ===
      correct[correct.length - 1 - suffixLen].toLowerCase()
  ) {
    suffixLen++
  }

  const prefix = correct.slice(0, prefixLen)
  const changed = correct.slice(prefixLen, correct.length - suffixLen)
  const suffix = correct.slice(correct.length - suffixLen)

  const segments = []
  if (prefix) segments.push({ text: prefix, changed: false })
  if (changed) segments.push({ text: changed, changed: true })
  if (suffix) segments.push({ text: suffix, changed: false })

  if (!segments.some((seg) => seg.changed)) {
    return [{ text: correct, changed: true }]
  }
  return segments
}

export function collectClozeItemsFromVocList (vocList) {
  const items = []
  for (const voc of vocList || []) {
    const englishText = (voc.englishText || '').trim()
    if (!englishText) continue
    const examples = extractExampleSentences(voc.chineseText)
    if (!examples.length) continue
    let exampleSentence = examples[0]
    for (const example of examples) {
      if (findWordFormInSentence(example, englishText)) {
        exampleSentence = example
        break
      }
    }
    const vocabularyId = Number(voc.vocabularyId || voc.id)
    items.push({
      englishText,
      chineseText: (voc.chineseText || '').trim(),
      exampleSentence,
      vocabularyId: Number.isFinite(vocabularyId) && vocabularyId > 0 ? vocabularyId : null
    })
  }
  return items
}

export function collectWrongItemsFromGroups (groups) {
  const wrongItems = []
  const seen = new Set()
  for (const group of groups || []) {
    for (const slot of group.slots || []) {
      if (slot.isCorrect) continue
      const key = `${slot.baseWord || ''}|||${slot.sentenceFull || ''}`
      if (seen.has(key)) continue
      seen.add(key)
      wrongItems.push({
        englishText: slot.baseWord || '',
        chineseText: slot.chineseText || '',
        exampleSentence: slot.sentenceFull || '',
        vocabularyId: slot.vocabularyId ?? null
      })
    }
  }
  return wrongItems
}

export function nirvanaSetName (setName) {
  const name = String(setName || '').trim()
  if (!name) return 'Nirvana'
  if (/\bnirvana\b/i.test(name) || name.includes('Nirvana')) return name
  return `${name} · Nirvana`
}

export function buildClozeGroups (items, groupSize = 5) {
  const validItems = items.filter((item) => {
    const baseWord = (item.englishText || '').trim()
    const sentence = (item.exampleSentence || '').trim()
    return baseWord && sentence
  })

  const rawGroups = chunkArrayForCloze(validItems, groupSize)
  return rawGroups.map((groupItems, groupIndex) => {
    const slots = groupItems.map((item, slotIndex) => {
      const baseWord = item.englishText.trim()
      const sentence = item.exampleSentence.trim()
      const slot = resolveClozeSlot(sentence, baseWord)
      const uniqueKey = item.vocabularyId != null
        ? `${groupIndex}-${slotIndex}-${item.vocabularyId}`
        : `${groupIndex}-${slotIndex}-${baseWord}-${sentence}`
      return {
        slotIndex,
        itemKey: uniqueKey,
        vocabularyId: item.vocabularyId ?? item.vocId ?? null,
        baseWord: slot.baseWord,
        chineseText: item.chineseText || '',
        sentenceFull: sentence,
        sentenceBefore: slot.sentenceBefore,
        sentenceAfter: slot.sentenceAfter,
        correctForm: slot.correctForm,
        matchedInSentence: slot.matchedInSentence,
        assignedWordKey: null
      }
    })

    const wordBank = shuffleArray(
      slots.map((slot) => ({
        wordKey: slot.itemKey,
        baseWord: getPrimaryBaseWord(slot.baseWord),
        placedSlotIndex: null
      }))
    )

    return {
      groupIndex,
      slots,
      wordBank
    }
  })
}

export function countClozeExerciseItems (items, groupSize = 5) {
  return buildClozeGroups(items, groupSize).reduce(
    (sum, group) => sum + group.slots.length,
    0
  )
}

/** Empty question template for POST /VocClozeAssignment. */
export function buildVocClozeAssignmentPayload ({
  payload,
  groups,
  studentId,
  teacherId,
  groupSize,
  timeLimitSeconds
}) {
  const sid = Number(studentId) || 0
  const tid = Number(teacherId) || sid
  return {
    teacherId: tid,
    studentId: sid,
    studentIds: sid ? [sid] : [],
    subjectName: payload?.subjectName || '',
    setName: payload?.setName || '',
    type: payload?.type || '选词填空',
    groupSize: Number(groupSize) || 5,
    timeLimitSeconds: Number(timeLimitSeconds) || DEFAULT_CLOZE_TIME_LIMIT_SECONDS,
    groups: (groups || []).map((group, index) => ({
      groupIndex: group.groupIndex ?? index,
      slots: (group.slots || []).map((slot) => ({
        slotIndex: slot.slotIndex,
        itemKey: slot.itemKey || '',
        vocabularyId: Number(slot.vocabularyId) || 0,
        baseWord: slot.baseWord || '',
        chineseText: slot.chineseText || '',
        sentenceFull: slot.sentenceFull || '',
        sentenceBefore: slot.sentenceBefore || '',
        sentenceAfter: slot.sentenceAfter || '',
        correctForm: slot.correctForm || '',
        matchedInSentence: !!slot.matchedInSentence,
        assignedWordKey: null
      })),
      wordBank: (group.wordBank || []).map((word) => ({
        wordKey: word.wordKey || '',
        baseWord: word.baseWord || '',
        placedSlotIndex: null
      }))
    }))
  }
}

export function normalizeAssignmentGroups (groups) {
  return (groups || []).map((group, index) => ({
    groupIndex: group.groupIndex ?? index,
    slots: (group.slots || []).map((slot, slotIndex) => ({
      slotIndex: slot.slotIndex ?? slotIndex,
      itemKey: slot.itemKey || `${index}-${slot.slotIndex ?? slotIndex}-${slot.vocabularyId || slot.baseWord}`,
      vocabularyId: slot.vocabularyId ?? null,
      baseWord: slot.baseWord || '',
      chineseText: slot.chineseText || '',
      sentenceFull: slot.sentenceFull || '',
      sentenceBefore: slot.sentenceBefore || '',
      sentenceAfter: slot.sentenceAfter || '',
      correctForm: slot.correctForm || '',
      matchedInSentence: !!slot.matchedInSentence,
      assignedWordKey: null
    })),
    wordBank: (group.wordBank || []).map((word) => ({
      wordKey: word.wordKey || '',
      baseWord: word.baseWord || '',
      placedSlotIndex: null
    }))
  }))
}

export function itemsFromAssignmentGroups (groups) {
  const items = []
  for (const group of groups || []) {
    for (const slot of group.slots || []) {
      items.push({
        englishText: slot.baseWord,
        chineseText: slot.chineseText || '',
        exampleSentence: slot.sentenceFull || '',
        vocabularyId: slot.vocabularyId ?? null
      })
    }
  }
  return items
}

function studentAnswerForSlot (slot, group) {
  if (!slot?.assignedWordKey) return null
  const word = (group?.wordBank || []).find((w) => w.wordKey === slot.assignedWordKey)
  return word ? word.baseWord : null
}

/** Map in-page cloze answers to POST /VocStudentClozeTest body. */
export function buildVocStudentClozeTestPayload ({
  payload,
  groups,
  groupResults,
  studentId,
  createdBy,
  isNirvanaSession,
  groupSize,
  timeLimitSeconds,
  totalElapsedSeconds,
  totalCorrect,
  totalQuestions,
  accuracyPercent
}) {
  return {
    studentId: Number(studentId) || 0,
    createdBy: Number(createdBy) || 0,
    subjectName: payload?.subjectName || '',
    setName: payload?.setName || '',
    type: payload?.type || '选词填空',
    status: 'finished',
    isNirvanaSession: !!isNirvanaSession,
    groupSize: Number(groupSize) || 5,
    timeLimitSeconds: Number(timeLimitSeconds) || DEFAULT_CLOZE_TIME_LIMIT_SECONDS,
    totalElapsedSeconds: Number(totalElapsedSeconds) || 0,
    totalCorrect: Number(totalCorrect) || 0,
    totalQuestions: Number(totalQuestions) || 0,
    accuracyPercent: Number(accuracyPercent) || 0,
    groupResults: (groups || []).map((group, index) => {
      const result =
        (groupResults || []).find((item) => item.groupIndex === group.groupIndex) || {}
      return {
        groupIndex: group.groupIndex ?? index,
        correct: result.correct ?? (group.slots || []).filter((slot) => slot.isCorrect).length,
        total: result.total ?? (group.slots || []).length,
        timedOut: !!result.timedOut,
        elapsedSeconds: Number(result.elapsedSeconds) || 0,
        slots: (group.slots || []).map((slot) => ({
          slotIndex: slot.slotIndex,
          vocabularyId: Number(slot.vocabularyId) || 0,
          baseWord: slot.baseWord || '',
          correctForm: slot.correctForm || '',
          studentAnswer: studentAnswerForSlot(slot, group),
          isCorrect: !!slot.isCorrect,
          sentenceFull: slot.sentenceFull || '',
          sentenceBefore: slot.sentenceBefore || '',
          sentenceAfter: slot.sentenceAfter || '',
          matchedInSentence: !!slot.matchedInSentence
        }))
      }
    })
  }
}

export function buildVocClozeAssignmentResultPayload (opts) {
  return {
    ...buildVocStudentClozeTestPayload(opts),
    assignmentId: Number(opts.assignmentId) || 0
  }
}

export function formatClozeDuration (seconds) {
  const total = Math.max(0, Number(seconds) || 0)
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m} 分 ${s} 秒`
}

export function formatClozeDateTime (value) {
  if (!value) return '—'
  const text = String(value).replace('T', ' ')
  return text.length >= 16 ? text.slice(0, 16) : text
}
